import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

import { ChevronButton } from './ChevronButton';
import styles from './PlanCarousel.module.scss';

export type CarouselOption<T = void> = {
  id: T;
  label: string;
};

const OPTION_WIDTH = 136;

export const PlanCarousel = <T,>({
  options,
  setCurrentOption,
  currentOptionId,
}: {
  options: CarouselOption<T>[];
  setCurrentOption: Dispatch<SetStateAction<T | undefined>>;
  currentOptionId?: T;
}) => {
  const firstRenderRef = useRef(true);

  const LOWER_BOUND = 0;
  const UPPER_BOUND = options.length - 1;

  const currentOptionIndex = options.findIndex(
    (option) => option.id === currentOptionId
  );

  const [currentPageIndex, setCurrentPageIndex] = useState(
    currentOptionIndex ?? 0
  );
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollLeftAmount = currentPageIndex * OPTION_WIDTH;

  const clampIndexToBounds = (i: number) =>
    Math.max(Math.min(i, UPPER_BOUND), LOWER_BOUND);

  const handleLeftArrowClick = () => {
    const newPageIndex = clampIndexToBounds(currentPageIndex - 1);
    setCurrentPageIndex(newPageIndex);
    setCurrentOption(options[newPageIndex].id);
  };

  const handleRightArrowClick = () => {
    const newPageIndex = clampIndexToBounds(currentPageIndex + 1);
    setCurrentPageIndex(newPageIndex);
    setCurrentOption(options[newPageIndex].id);
  };

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scroll({
        top: 0,
        left: scrollLeftAmount,
        behavior: firstRenderRef.current ? 'auto' : 'smooth',
      });
    }

    if (firstRenderRef.current) {
      firstRenderRef.current = false;
    }
  }, [scrollLeftAmount]);

  useEffect(() => {
    // Make sure to reset current option if options changed
    const newPageIndex = clampIndexToBounds(currentPageIndex);

    if (newPageIndex !== currentPageIndex) {
      setCurrentPageIndex(newPageIndex);
    }

    setCurrentOption(options[newPageIndex].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  return (
    <div className="d-flex">
      <ChevronButton
        disabled={currentPageIndex <= LOWER_BOUND}
        onClick={handleLeftArrowClick}
      />
      <div className={styles.scrollContainer} ref={scrollContainerRef}>
        {options.map(({ label, id }) => (
          <a
            href="#coverage-overview"
            className={`wmn2 br8 tc-purple-500 fw-bold ta-center ${styles.option}`}
            key={`PLAN_CAROUSEL_OPTION:${id}`}
          >
            {label}
          </a>
        ))}
      </div>
      <ChevronButton
        direction="RIGHT"
        disabled={currentPageIndex >= UPPER_BOUND}
        onClick={handleRightArrowClick}
      />
    </div>
  );
};
